import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AbstractControl, FormControl, NgForm} from "@angular/forms";
import {MatTabGroup} from "@angular/material/tabs";
import {Observable, of, ReplaySubject} from "rxjs";
import {debounceTime, distinctUntilChanged, startWith, switchMap} from "rxjs/operators";
import {SearchModel} from "../../model/search-model";
import {SearchResultModel} from "../../model/search-result-model";
import {State} from "../../model/state";
import {AutocompleteService} from "../../service/autocomplete.service";
import {StateService} from "../../service/state.service";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {RecentSearch} from "../../model/RecentSearch";

@Component({
  selector: 'app-search-panel',
  templateUrl: './search-panel.component.html',
  styleUrls: ['./search-panel.component.scss']
})
export class SearchPanelComponent implements OnInit, AfterViewInit, OnChanges {

  @Output()
  processSearch = new EventEmitter<SearchModel>();

  @Output()
  clearSearch = new EventEmitter<any>();

  @Input()
  recentSearch: RecentSearch;

  @ViewChild('tabs') tabGroup: MatTabGroup;

  @ViewChild('cusipInput') cusipInput: ElementRef;
  @ViewChild('form') form: NgForm;

  filteredObligors: Observable<Array<SearchResultModel>>;
  filteredCusips: Observable<Array<SearchResultModel>>;
  fcObligorNameInput = new FormControl();
  fcStateAbbrInput = new FormControl();
  fcCusipInput = new FormControl();

  states: Array<State> = undefined;
  filteredStates: Observable<Array<State>>;

  private statesInitialized = new ReplaySubject(1);

  constructor(private autocompleteService: AutocompleteService,
              private stateService: StateService) {
    this.stateService.getStateList().subscribe(response => {
      this.states = response;
      this.statesInitialized.next(true);
    });
  }

  ngOnInit(): void {
    // Init search object
    this.recentSearch = new RecentSearch("", null, "");
    if (this.recentSearch.state) {
      this.filteredStates = this.fcStateAbbrInput.valueChanges.pipe(
        startWith(this.recentSearch.state.stateAbbr),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(val => {
          return this.filterStates(val || '')
        })
      );
    }
    this.filteredObligors = this.fcObligorNameInput.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(val => {
        return this.filterObligors(val || '')
      })
    );
  }

  public displayFn(value?: string) {
    this.clearErrors(this.fcObligorNameInput);
    this.clearErrors(this.fcStateAbbrInput);
    return value ? this.states.find(st => st.stateName === value).stateName : undefined;
  }

  ngAfterViewInit(): void {
    this.statesInitialized.subscribe(() => {
      if (this.recentSearch?.keywords || this.recentSearch?.cusip) {
        this.validateStateInput();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.recentSearch && this.recentSearch) {
      this.statesInitialized.subscribe(m => {
        this.repeatSearch();
      });
    }
  }

  filterObligors(val: string): Observable<Array<SearchResultModel>> {
    if (val.length > 1) {
      const model = new SearchModel();
      model.obligorName = val;
      model.stateAbbr = this.recentSearch?.state?.stateAbbr;
      return this.autocompleteService.autocompleteObligors(model);
    }
    return of([]);
  }

  private filterStates(val: any): Observable<Array<State>> {
    if (!this.states || this.states.length === 0) return of();
    const filterValue = val.toLowerCase();
    return of(this.states.filter(state => state.stateName.toLowerCase().includes(filterValue) || state.stateAbbr.toLowerCase().includes(filterValue)));
  }

  validateStateInput() {
    this.clearErrors(this.fcObligorNameInput);
    this.clearErrors(this.fcStateAbbrInput);
    const stateInput = this.recentSearch?.state?.stateAbbr?.toLowerCase();
    const filteredState = this.states.filter(state => state.stateAbbr.toLowerCase() === stateInput || state.stateName.toLowerCase() === stateInput);
    if (filteredState.length === 1) {
      this.recentSearch.state.stateAbbr = filteredState[0].stateAbbr;
      return;
    }
    // this.recentSearch.state.stateAbbr = undefined;
  }

  clearErrors(control: AbstractControl): void {
    control.markAsUntouched();
    control.markAsPristine();
    control.setErrors(null);
  }

  repeatSearch(): void {
    let valid: boolean;
    if (this.recentSearch.keywords || this.recentSearch.state?.stateId) {
      this.tabGroup.selectedIndex = 0;
      this.fcObligorNameInput.patchValue(this.recentSearch.keywords);
      if (this.recentSearch.state?.stateId) {
        this.recentSearch.state = this.states.filter(m => m.stateId === this.recentSearch.state.stateId)[0];
        this.fcStateAbbrInput.patchValue(this.recentSearch.state.stateName);
      } else {
        this.clearControl(this.fcStateAbbrInput);
      }
      this.fcObligorNameInput.markAsTouched();
      valid = this.fcObligorNameInput.valid;
    } else {
      this.tabGroup.selectedIndex = 1;
      this.fcCusipInput.patchValue(this.recentSearch.cusip);
      this.fcCusipInput.markAsTouched();
      valid = this.fcCusipInput.valid;
    }
    if (valid) {
      setTimeout(() => this.form.ngSubmit.emit(this.recentSearch));
    }
  }

  clearForm(): void {
    if (this.tabGroup.selectedIndex === 0) {
      this.recentSearch.keywords = undefined;
      this.recentSearch.state = undefined;
      this.clearControl(this.fcStateAbbrInput);
      this.clearControl(this.fcObligorNameInput);
    } else {
      this.recentSearch.cusip = undefined;
      this.clearControl(this.fcCusipInput);
    }
    this.clearSearch.emit();
  }

  clearControl(control: AbstractControl): void {
    control.markAsUntouched();
    control.markAsPristine();
    control.setValue(null);
    control.setErrors(null);
  }

  submit(): void {
    let model = new SearchModel();
    if (this.tabGroup.selectedIndex === 0) {
      // obligor search
      if (!this.validateObligorInput()) return;
      model.obligorName = this.fcObligorNameInput.value;
      if (this.fcStateAbbrInput.value) {
        model.stateAbbr = this.states.find(m => m.stateName === this.fcStateAbbrInput.value).stateAbbr;
        model.stateId = this.states.find(m => m.stateName === this.fcStateAbbrInput.value).stateId.toString();
      }
    } else {
      // cusip search
      model.cusip = this.fcCusipInput.value;
    }
    this.processSearch.emit(model);
  }

  validateObligorInput(): boolean {
    if (!this.fcObligorNameInput.value && !this.fcStateAbbrInput.value) {
      this.fcObligorNameInput.setErrors({'incorrect': true});
      this.fcStateAbbrInput.setErrors({'incorrect': true});
      return false;
    }
    return true;
  }

  onAutocompleteObligorSelect($event: MatAutocompleteSelectedEvent) {
    setTimeout(() => {
      this.recentSearch.keywords = $event.option.value.obligorName;
    }, 0)
  }

}
